import { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import { captureImageException } from 'Utilities/captureException/captureImageException';
import { formatImageURLWithMeta } from 'Utilities/formatters';
import proPropTypes from 'Utilities/proPropTypes';

import styles from './BasicThumbnail.module.scss';

const BasicThumbnail = ({
	alt = '',
	authenticated = false,
	className,
	height = 174,
	image,
	imgAttributes = {},
	rootComponent,
	width = 232,
	...props
}) => {
	const [hasError, setHasError] = useState(false);
	const thumbnailPreset = image?.presets?.find((preset) => preset.name === 'thumbnail');

	const thumbnailImage = thumbnailPreset || image;

	const formatUrl = ({ dpr } = {}) =>
		`${formatImageURLWithMeta(thumbnailImage, {
			blur: authenticated ? 0 : 360,
			dpr: dpr || 1,
			h: height,
			w: width,
		})}${dpr ? ` ${dpr}x` : ''}`;

	const imageSrc = formatUrl();

	const onError = (event) => {
		setHasError(true);
		captureImageException({
			event,
			imageComponent: 'BasicThumbnail',
			rootComponent,
		});
	};

	// We don't need srcsets for thumbnail images as they are already formatted
	const srcSet = !thumbnailPreset ? [1, 2, 3].map((dpr) => formatUrl({ dpr })).join(', ') : null;

	return (
		<div className={cx(styles.component, className, { [styles.blur]: !authenticated })} {...props}>
			<div className={cx({ [styles.placeholder]: hasError })}>
				{!hasError && (
					<img alt={alt} loading="lazy" {...imgAttributes} onError={onError} src={imageSrc} srcSet={srcSet} />
				)}
			</div>
		</div>
	);
};

BasicThumbnail.propTypes = {
	alt: PropTypes.string,
	authenticated: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
	className: proPropTypes.className,
	height: PropTypes.number,
	image: PropTypes.shape({
		presets: PropTypes.arrayOf(
			PropTypes.shape({
				name: PropTypes.string,
				url: PropTypes.string,
			}),
		),
		url: PropTypes.string,
	}).isRequired,
	imgAttributes: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool])),
	width: PropTypes.number,
};

export default BasicThumbnail;
